import React, { useEffect, useMemo, useState } from 'react';
import {
  Header, Breadcrumb, SupportCard, ST, fmtPrice, fxStamp,
  countryName, flagEmoji, track, useT, BUILD_VERSION,
  carrierLogo, useLogoManifest,
} from './Chrome';
import { PlanDetailModal, durationLabel } from './PlanModal';
import { PINNED_HEROES, heroStyle, resolveHero } from './hero';
import { SLUG_BY_ISO } from '../../lib/countrySlugs';
import { PayBox, TrustBadge, FaqSection, SiteFooterV2 } from './Footers';
import { RequestProductLink } from './RequestProduct';

///////////////////////////////////////////////////////////////////////////////
// /esim/<country> landing page — SEO target "esim data plan in <country>".
//
// Layout (approved mockup docs/esim-landing-mockup.html):
//   1. compact hero (title only) — the size grid IS the hero
//   2. square size-category cards, no prices (1 GB · 7 Days, 30 Days
//      Unlimited, …) built from the live catalog
//   3. clicking a size collapses the grid and expands that size in place
//      (the carrier-tile pattern from /topup/<country>), showing priced
//      options grouped: <country> only / regional / global. Deep links
//      /esim/<slug>/<sizeSlug> land pre-expanded.
//   4. Buy opens the shared PlanDetailModal (purchase details) → checkout
//   5. marketing strip, refund policy, detail sections and FAQ below —
//      all translated (useT) and priced in the display currency (fmtPrice).
///////////////////////////////////////////////////////////////////////////////

const GB = 1024 * 1024 * 1024;

// "1GB" -> "1", "512MB" -> "512mb", "1.5GB" -> "1-5"; used in /esim/<c>/<size>
function dataSlugOf(label) {
  const s = String(label || '').trim().toLowerCase();
  const m = s.match(/^([\d.]+)\s*(gb|mb)$/);
  if (m) {
    const num = m[1].replace('.', '-');
    return m[2] === 'gb' ? num : `${num}mb`;
  }
  return s.replace(/[^a-z0-9]+/g, '') || 'plan';
}

function sizeSlugOf(p) {
  return p.is_daily ? `daily_${dataSlugOf(p.data_label)}` : `${dataSlugOf(p.data_label)}_${p.duration || 0}`;
}

// Deep links can carry a day count for daily plans:
// "daily_1_22" -> { key: 'daily_1', days: 22 } (1 GB/day for 22 days).
// Fixed-size slugs ("3_30") pass through untouched.
function parsePlanSlug(slug) {
  if (!slug) return { key: null, days: null };
  const m = /^(daily_[a-z0-9-]+?)_(\d+)$/.exec(slug);
  if (m) return { key: m[1], days: Math.min(90, Math.max(1, parseInt(m[2], 10))) };
  return { key: slug, days: null };
}

// "1GB" -> "1 GB" for the big tile number
function splitDataLabel(label) {
  const m = String(label || '').match(/^([\d.]+)\s*(GB|MB)$/i);
  return m ? { num: m[1], unit: ` ${m[2].toUpperCase()}` } : null;
}

// Multi-country plans covering ~a region vs the whole world
const GLOBAL_MIN_COUNTRIES = 30;

function locationCount(p) {
  if (typeof p.location !== 'string') return 1;
  return p.location.split(',').filter(Boolean).length;
}

// Substitute {c}/{p}/{n} placeholders in a translated string
function fill(s, vars) {
  return String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
}

export default function EsimLanding({ me, currency, onCurrency, landing, planSlug }) {
  const t = useT();
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(false);
  const [openKey, setOpenKey] = useState(() => parsePlanSlug(planSlug).key);
  // Day count from a daily deep link (/esim/<c>/daily_1_22) — preselects the
  // days in the purchase modal.
  const [presetDays] = useState(() => parsePlanSlug(planSlug).days);
  const [showAll, setShowAll] = useState(false);
  const [detail, setDetail] = useState(null); // { p, full, error }
  const cname = landing.name;
  useLogoManifest(); // redraw once the carrier-logo manifest lands

  // Same country hero photo as the topup landing pages (docs/CARRIER_LOGOS.md)
  const [heroUrl, setHeroUrl] = useState(() => (
    PINNED_HEROES[landing.iso] !== undefined ? PINNED_HEROES[landing.iso] : null
  ));
  useEffect(() => {
    if (PINNED_HEROES[landing.iso] !== undefined) return undefined;
    let dead = false;
    resolveHero(landing.name, (url) => { if (!dead) setHeroUrl(url); }, landing.iso);
    return () => { dead = true; };
  }, [landing.iso]);

  useEffect(() => {
    let dead = false;
    track('country_view', { country: landing.iso, product_type: 'esim' });
    fetch(`/api/esims/catalog?country=${landing.iso}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (!dead) setProducts(data.products); })
      .catch(() => { if (!dead) setError(true); });
    return () => { dead = true; };
  }, [landing.iso]);

  // Size categories from the live catalog. Categories with a single-country
  // plan come first; sizes only available as regional/global follow.
  const cats = useMemo(() => {
    if (!products) return null;
    const map = new Map();
    for (const p of products) {
      const key = sizeSlugOf(p);
      let c = map.get(key);
      if (!c) {
        c = {
          key,
          data_label: p.data_label,
          data_bytes: p.data_bytes || 0,
          duration: p.duration,
          duration_unit: p.duration_unit,
          is_daily: p.is_daily,
          single: [],
          multi: [],
        };
        map.set(key, c);
      }
      (p.is_regional ? c.multi : c.single).push(p);
    }
    const list = [...map.values()];
    for (const c of list) {
      c.single.sort((a, b) => a.price - b.price);
      c.multi.sort((a, b) => locationCount(a) - locationCount(b) || a.price - b.price);
      c.from = Math.min(...[...c.single, ...c.multi].map((p) => p.price));
    }
    list.sort((a, b) => (b.single.length ? 1 : 0) - (a.single.length ? 1 : 0)
      || (a.is_daily ? 1 : 0) - (b.is_daily ? 1 : 0)
      || a.data_bytes - b.data_bytes
      || (a.duration || 0) - (b.duration || 0));

    // Badges: best per-GB single plan → "Best value"; the size with the most
    // options tends to be the size everyone stocks → "Popular".
    let best = null;
    for (const c of list) {
      if (c.is_daily) continue; // per-day prices skew the $/GB comparison
      for (const p of c.single) {
        if (p.per_gb != null && (!best || p.per_gb < best.per_gb)) best = { per_gb: p.per_gb, key: c.key };
      }
    }
    let popular = null;
    for (const c of list) {
      const n = c.single.length + c.multi.length;
      if (c.single.length && (!popular || n > popular.n)) popular = { n, key: c.key };
    }
    for (const c of list) {
      c.bestValue = best && best.key === c.key;
      c.popular = !c.bestValue && popular && popular.key === c.key;
    }
    return list;
  }, [products]);

  const openCat = cats && openKey ? cats.find((c) => c.key === openKey) : null;

  // Keep the URL in step with the expanded size so links are shareable and
  // browser back works: /esim/<slug> ⇄ /esim/<slug>/<sizeSlug>
  function openSize(c) {
    setOpenKey(c.key);
    track('plan_view', { country: landing.iso, product_type: 'esim', carrier: c.key });
    try { window.history.pushState({}, '', `/esim/${landing.slug}/${c.key}`); } catch (e) { /* ignore */ }
    window.scrollTo({ top: 0 });
  }
  function closeSize() {
    setOpenKey(null);
    try { window.history.pushState({}, '', `/esim/${landing.slug}`); } catch (e) { /* ignore */ }
  }
  useEffect(() => {
    function onPop() {
      const m = /^\/esim\/[a-z0-9-]+\/([a-z0-9_-]+)/.exec(window.location.pathname);
      setOpenKey(m ? parsePlanSlug(m[1]).key : null);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Guests buy without an account — /checkout collects the email. Daily plans
  // pick their length in the detail modal first.
  function startBuy(p, days) {
    if (p.is_daily && days == null) return openDetail(p);
    const qs = new URLSearchParams({ type: 'esim', code: p.slug });
    if (p.is_daily) qs.set('days', String(days));
    window.location.href = `/checkout?${qs.toString()}`;
  }

  function openDetail(p) {
    setDetail({ p, full: null, error: false });
    track('plan_view', { country: landing.iso, product_type: 'esim', carrier: p.operator || p.slug });
    fetch(`/api/esims/product/${encodeURIComponent(p.slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        setDetail((d) => (d && d.p.slug === p.slug ? { ...d, full: data } : d));
      })
      .catch(() => {
        setDetail((d) => (d && d.p.slug === p.slug ? { ...d, error: true } : d));
      });
  }

  const fromPrice = useMemo(() => {
    if (!products || !products.length) return null;
    return fmtPrice(Math.min(...products.map((p) => p.price)));
  }, [products, fxStamp()]);

  // Network names seen across this country's single plans, for the details
  // section — real coverage data beats hand-written copy.
  const networks = useMemo(() => {
    if (!products) return [];
    const seen = new Map();
    for (const p of products) {
      if (p.is_regional || !p.networks) continue;
      for (const n of p.networks) {
        if (n.name && !seen.has(n.name)) seen.set(n.name, n.type || '');
      }
    }
    return [...seen.entries()].slice(0, 6);
  }, [products]);

  const faq = useMemo(() => [1, 2, 3, 4, 5, 6, 7, 8].map((i) => [
    fill(t(`el_faq${i}q`), { c: cname }),
    fill(t(`el_faq${i}a`), { c: cname }),
  ]), [t, cname]);

  // FAQPage + BreadcrumbList structured data — the FAQ answers Google shows
  // under "esim data plan in <country>" queries.
  const jsonLd = useMemo(() => JSON.stringify([
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'eSIM Data Plans', item: 'https://mobiletopup.com/dataplans' },
        { '@type': 'ListItem', position: 2, name: `${cname} eSIM`, item: `https://mobiletopup.com/esim/${landing.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map(([q, a]) => ({
        '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ]), [faq, cname, landing.slug]);

  const shownCats = cats ? (showAll ? cats : cats.slice(0, 12)) : null;
  const total = products ? products.length : 0;

  function catTitle(c) {
    if (!c) return '';
    const d = splitDataLabel(c.data_label);
    const dataPart = d ? `${d.num}${d.unit}` : (c.data_label || t('el_unlimited'));
    return c.is_daily ? `${dataPart} ${t('el_per_day')}` : `${dataPart} · ${c.duration} ${c.duration > 1 ? t('el_days') : t('el_day')}`;
  }

  function OptionRow({ p, grp }) {
    // Network logo from the carrier CDN store (docs/CARRIER_LOGOS.md) for
    // country-only plans — multi-country rows keep the group's globe.
    const netName = !p.is_regional && p.networks && p.networks[0] && p.networks[0].name;
    const logo = netName ? carrierLogo(netName) : null;
    return (
      <div className="optrow" role="button" tabIndex={0}
        onClick={() => openDetail(p)}
        onKeyDown={(e) => { if (e.key === 'Enter') openDetail(p); }}>
        {logo && <img className="ologo" src={logo} alt={netName} loading="lazy" />}
        <div className="omain">
          <div className="oprice">
            {fmtPrice(p.price)}
            {p.is_daily && <em> {t('el_per_day')}</em>}
          </div>
          <div className="ometa">
            {grp}{' · '}{p.name}
            {p.per_gb != null && !p.is_daily && ` · $${p.per_gb.toFixed(2)}/GB`}
          </div>
          <div className="odeliv">{t('el_qr_instant')}</div>
        </div>
        <button type="button" className="btn small"
          onClick={(e) => { e.stopPropagation(); openDetail(p); }}>
          {t('el_buy')}
        </button>
      </div>
    );
  }

  const regional = openCat ? openCat.multi.filter((p) => locationCount(p) < GLOBAL_MIN_COUNTRIES) : [];
  const global = openCat ? openCat.multi.filter((p) => locationCount(p) >= GLOBAL_MIN_COUNTRIES) : [];

  return (
    <div className="page">
      <Header me={me} active="esim" currency={currency} onCurrency={onCurrency} />

      {/* Compact hero — country photo like the topup pages, title only;
          the grid is the hero */}
      <section className="hero esim-landing-hero" style={heroStyle(heroUrl)}>
        <Breadcrumb active="esim" iso={landing.iso} countryName={cname} />
        <h1 className="hero-title">
          {flagEmoji(landing.iso)} <b>{fill(t('el_h1'), { c: cname })}</b>
        </h1>
      </section>

      <div className="body">
        <main className="main">
          {!openCat && (
            <div>
              <div className="sortrow">
                <span className="count">
                  {products
                    ? <b>{fill(t('el_count'), { n: total, c: cname })}</b>
                    : `${t('esim_loading')}`}
                </span>
                {products != null && <span>· {t('el_pick')}</span>}
                <span className="paynote">{t('el_prices_in')} {currency || 'USD'}</span>
              </div>

              {error && (
                <div className="notice">
                  {t('el_error')}{' '}
                  <button type="button" className="linkish" onClick={() => window.location.reload()}>{t('el_retry')}</button>
                </div>
              )}

              {!error && (
                <div className="esim-tiles">
                  {cats == null && [...Array(8)].map((_, i) => <div key={i} className="esim-tile skeleton" />)}
                  {shownCats && shownCats.map((c) => {
                    const d = splitDataLabel(c.data_label);
                    return (
                      <a
                        key={c.key}
                        className={`esim-tile${d ? '' : ' unl'}`}
                        href={`/esim/${landing.slug}/${c.key}`}
                        onClick={(e) => { e.preventDefault(); openSize(c); }}
                      >
                        {c.popular && <span className="badge">{t('el_popular')}</span>}
                        {c.bestValue && <span className="badge value">{t('el_best_value')}</span>}
                        {d
                          ? <span className="gb">{d.num}<small>{d.unit}</small></span>
                          : <span className="gb">{c.data_label || t('el_unlimited')}</span>}
                        <span className="days">
                          {c.is_daily
                            ? t('el_daily_badge')
                            : `${c.duration} ${c.duration > 1 ? t('el_days') : t('el_day')}`}
                        </span>
                        {c.is_daily && <span className="fup">{t('el_daily_note')}</span>}
                      </a>
                    );
                  })}
                </div>
              )}

              {cats != null && cats.length === 0 && !error && (
                <div className="notice">
                  {fill(t('el_no_plans'), { c: cname })}{' '}
                  <a href="/dataplans">{t('el_browse_all')}</a>
                  {' · '}
                  <RequestProductLink label="Request this destination" defaultType="esim" country={iso} />
                </div>
              )}

              {cats != null && cats.length > 12 && (
                <div className="esim-more">
                  <button type="button" className="linkish" onClick={() => setShowAll((v) => !v)}>
                    {showAll ? t('el_show_less') : fill(t('el_show_all'), { n: cats.length })}
                  </button>
                </div>
              )}
            </div>
          )}

          {openCat && (
            <div>
              <button type="button" className="sp-back" onClick={closeSize}>‹ {t('el_all_plans')}</button>
              <div className="size-panel">
                <div className="sp-band">
                  <span className="sp-flag">{flagEmoji(landing.iso)}</span>
                  <span className="sp-title">{catTitle(openCat)} — {cname} eSIM</span>
                </div>
                <div className="sp-body">
                  {openCat.single.length > 0 && (
                    <>
                      <div className="optgrp">{flagEmoji(landing.iso)} {fill(t('el_country_only'), { c: cname })}</div>
                      {openCat.single.map((p) => <OptionRow key={p.slug} p={p} grp={fill(t('el_country_only'), { c: cname })} />)}
                    </>
                  )}
                  {regional.length > 0 && (
                    <>
                      <div className="optgrp">🌏 {fill(t('el_regional_grp'), { c: cname })}</div>
                      {regional.map((p) => <OptionRow key={p.slug} p={p} grp={fill(t('el_countries_n'), { n: locationCount(p) })} />)}
                    </>
                  )}
                  {global.length > 0 && (
                    <>
                      <div className="optgrp">🌍 {fill(t('el_global_grp'), { c: cname })}</div>
                      {global.map((p) => <OptionRow key={p.slug} p={p} grp={fill(t('el_countries_n'), { n: locationCount(p) })} />)}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── marketing content below the grid ── */}
          <div className="below-fold">
            <p className="esim-sub">{fill(t('el_sub'), { p: fromPrice || '$2.50' })}</p>

            <div className="truststrip onwhite">
              <span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13 2 3 14h8l-1 8 10-12h-8l1-8z" /></svg>
                {t('el_trust_qr')}
              </span>
              <span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                {t('el_trust_refund')}
              </span>
              <span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2" /></svg>
                {t('el_trust_support')}
              </span>
            </div>

            <div className="steps-strip rounded">
              {[t('el_step1'), t('el_step2'), t('el_step3'), t('el_step4')].map((s, i) => (
                <div className="step" key={s}><span className="n">{i + 1}</span>{s}</div>
              ))}
            </div>

            <div className="proofrow inmain">
              <div className="proofstat"><div className="n">1M+</div><div className="l">{t('el_proof_orders')}</div></div>
              <div className="proofstat"><div className="n">{t('el_proof_since')}</div><div className="l">{t('el_proof_since_l')}</div></div>
              {networks.length > 1 && (
                <div className="proofstat"><div className="n">{fill(t('el_proof_networks'), { n: networks.length })}</div><div className="l">{networks.map(([n]) => n).join(', ')}</div></div>
              )}
              <div className="proofstat"><div className="n">{t('el_proof_refund')}</div><div className="l">{t('el_proof_refund_l')}</div></div>
            </div>
          </div>

          {/* ── refund policy ── */}
          <h2 className="landing-h2">{t('el_refund_h')}</h2>
          <div className="refundbox">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="m9 12 2 2 4-4" /></svg>
            <div>
              <b>{t('el_refund_title')}</b>
              <p>{t('el_refund_body')}</p>
            </div>
          </div>

          {/* ── SEO detail sections ── */}
          <div className="seo-sections">
            <h2 className="landing-h2">{fill(t('el_details_h'), { c: cname })}</h2>

            <h3>{t('el_networks_h')}</h3>
            <p>{fill(t('el_networks_p'), { c: cname })}</p>
            {networks.length > 0 && (
              <div className="netgrid">
                {networks.map(([name, type]) => {
                  const logo = carrierLogo(name);
                  return (
                    <div className="netcard" key={name}>
                      {logo && <img className="nlogo" src={logo} alt="" loading="lazy" />}
                      <b>{name}</b>
                      {type && <div className="nmeta">{type}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            <h3>{t('el_activation_h')}</h3>
            <ul>
              <li>{t('el_act1')}</li>
              <li>{t('el_act2')}</li>
              <li>{t('el_act3')}</li>
              <li>{t('el_act4')}</li>
              <li>{t('el_act5')}</li>
            </ul>

            <h3>{t('el_why_h')}</h3>
            <p>{fill(t('el_why_p'), { c: cname })}</p>
          </div>

          <p style={{ fontSize: 13, marginTop: 14 }}>
            {SLUG_BY_ISO[landing.iso] && (
              <a href={`/topup/${SLUG_BY_ISO[landing.iso]}`}>{fill(t('el_also_topup'), { c: cname })}</a>
            )}
            {' · '}
            <a href="/dataplans">{t('el_browse_all')}</a>
          </p>

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
        </main>

        <aside className="rail">
          <div className="railcard trust">
            <h4>{t('el_refund_h')}</h4>
            <b>{t('el_trust_refund')}</b> {t('el_refund_body').split('.')[0]}.
          </div>
          <SupportCard />
          {SLUG_BY_ISO[landing.iso] && (
            <div className="railcard">
              <h4>{t('nav_esim_data')}</h4>
              <a href={`/topup/${SLUG_BY_ISO[landing.iso]}`}>{fill(t('el_also_topup'), { c: cname })}</a>
            </div>
          )}
        </aside>
      </div>

      <div className="page-footer">
        <PayBox />
        <TrustBadge />
        <FaqSection faq={faq} seo={fill(t('el_seo'), { c: cname })} heading={t('el_faq_h')} />
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />

      {detail && <PlanDetailModal detail={detail} initialDays={presetDays} onClose={() => setDetail(null)} onBuy={startBuy} />}
    </div>
  );
}
